/**
 * Collapsible Activity (audit log) section, shared by the task, lead, and
 * opportunity slide-overs (Tabs-updates batch). Renders each entry as a
 * human-readable line — "Current action: Send outreach → Responded",
 * "Company name: Old Co → New Co" — plus a Brisbane timestamp, most-recent
 * first.
 *
 * This is the first slice of the deferred SlideOver.tsx extraction: three
 * call-sites previously duplicated (or lacked) this surface.
 */

import { useState } from 'react';

import type { AuditEntry } from '@workspace/shared';

/** Brisbane-formatted timestamp (DD Mon HH:mm) for an ISO string. */
function formatBrisbane(iso: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** Humanise an UPPER_SNAKE token into a readable phrase. */
function humanise(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Build a one-line description of an audit entry from its event type and
 * old/new payloads. Defensive — falls back to the humanised event type when
 * the payload shape is unfamiliar.
 */
function describe(entry: AuditEntry): string {
  const { eventType } = entry;

  if (eventType === 'FIELD_UPDATED' && entry.newValue && typeof entry.newValue === 'object') {
    const nv = entry.newValue as { label?: string; value?: string | null };
    const ov = entry.oldValue as { value?: string | null } | null;
    const from = ov?.value ?? '—';
    const to = nv.value ?? '—';
    return `${nv.label ?? 'Updated'}: ${from} → ${to}`;
  }

  // Scalar old/new transitions render "Label: from → to". CURRENT_STEP_CHANGED
  // is intentionally excluded — the legacy advance-step endpoint stored object
  // payloads there, so it falls through to a clean humanised label instead of
  // rendering "[object Object]".
  if (
    (eventType === 'CURRENT_ACTION_CHANGED' ||
      eventType === 'STAGE_CHANGED' ||
      eventType === 'NEXT_ACTION_CHANGED' ||
      eventType === 'STATUS_CHANGED' ||
      eventType === 'PRIORITY_CHANGED' ||
      eventType === 'TYPE_CHANGED') &&
    (entry.oldValue == null || typeof entry.oldValue === 'string') &&
    (entry.newValue == null || typeof entry.newValue === 'string')
  ) {
    const from = entry.oldValue == null ? '—' : humanise(String(entry.oldValue));
    const to = entry.newValue == null ? '—' : humanise(String(entry.newValue));
    const label =
      eventType === 'STAGE_CHANGED'
        ? 'Stage'
        : eventType === 'CURRENT_ACTION_CHANGED'
          ? 'Current action'
          : eventType === 'NEXT_ACTION_CHANGED'
            ? 'Next action'
            : eventType === 'STATUS_CHANGED'
              ? 'Status'
              : eventType === 'PRIORITY_CHANGED'
                ? 'Priority'
                : 'Type';
    return `${label}: ${from} → ${to}`;
  }

  return humanise(eventType);
}

/**
 * The shared Activity section. Collapsed by default to keep the slide-over
 * compact; expanding reveals the entries. `eyebrowNum` lets each slide-over
 * keep its numbered-section sequence.
 */
export function AuditLog({ entries, eyebrowNum }: { entries: AuditEntry[]; eyebrowNum: string }) {
  const [open, setOpen] = useState(false);

  return (
    <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="eyebrow mono-label flex w-full items-center justify-between text-mx-grey-mid"
      >
        <span>
          <span className="num">{eyebrowNum}</span>
          Activity
        </span>
        <span aria-hidden>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <ul className="mt-mx-2 flex flex-col gap-mx-1">
          {entries.slice(0, 30).map((entry) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-mx-3 border-b border-mx-grey-800 py-mx-1 text-mx-body-sm"
            >
              <span className="text-mx-grey-300">{describe(entry)}</span>
              <span className="mono-label tnum shrink-0 text-mx-grey-500">
                {formatBrisbane(entry.occurredAt)}
              </span>
            </li>
          ))}
          {entries.length === 0 && (
            <li className="text-mx-body-sm text-mx-grey-mid">No activity yet.</li>
          )}
        </ul>
      )}
    </section>
  );
}
